import React from 'react';

export type VideoProvider = 'auto' | 'vertex' | 'gemini';

interface VideoProviderOption {
  value: VideoProvider;
  label: string;
  icon: string;
  description: string;
  requirements: string[];
  status?: 'available' | 'needs-config' | 'paid-only';
}

const providerOptions: VideoProviderOption[] = [
  {
    value: 'auto',
    label: '자동 선택',
    icon: '🤖',
    description: '사용 가능한 최적의 API 자동 선택',
    requirements: ['설정된 API 키 기반 자동 선택'],
    status: 'available'
  },
  {
    value: 'vertex',
    label: 'Vertex AI (Google Cloud)',
    icon: '☁️',
    description: 'Google Cloud Platform의 Vertex AI',
    requirements: ['Google Cloud API Key', 'Project ID', 'Pay-as-you-go'],
    status: 'needs-config'
  },
  {
    value: 'gemini',
    label: 'Gemini API (AI Studio)',
    icon: '✨',
    description: 'Google AI Studio의 Gemini API',
    requirements: ['Gemini API Key', 'Paid Tier 업그레이드 필요'],
    status: 'paid-only'
  }
];

interface VideoProviderSelectorProps {
  value: VideoProvider;
  onChange: (provider: VideoProvider) => void;
  disabled?: boolean;
}

export const VideoProviderSelector: React.FC<VideoProviderSelectorProps> = ({
  value,
  onChange,
  disabled = false
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const selectedOption = providerOptions.find(opt => opt.value === value) || providerOptions[0];

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'available': return 'text-green-400';
      case 'needs-config': return 'text-yellow-400';
      case 'paid-only': return 'text-orange-400';
      default: return 'text-slate-400';
    }
  };

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'available': return '✅ 사용 가능';
      case 'needs-config': return '⚙️ 설정 필요';
      case 'paid-only': return '💳 유료 플랜 필요';
      default: return '';
    }
  };

  return (
    <div className="mb-6">
      <label className="block text-sm font-medium text-slate-300 mb-2">
        🌐 API 제공자 선택
      </label>
      
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          disabled={disabled}
          className={`w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg 
            text-left flex items-center justify-between transition-all
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-slate-700 cursor-pointer'}
            ${isExpanded ? 'ring-2 ring-indigo-500' : ''}`}
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl">{selectedOption.icon}</span>
            <div>
              <div className="text-white font-medium">{selectedOption.label}</div>
              <div className="text-xs text-slate-400">{selectedOption.description}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-medium ${getStatusColor(selectedOption.status)}`}>
              {getStatusLabel(selectedOption.status)}
            </span>
            <svg 
              className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>

        {isExpanded && !disabled && (
          <div className="absolute z-10 w-full mt-2 bg-slate-800 border border-slate-600 rounded-lg shadow-xl overflow-hidden">
            {providerOptions.map(option => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setIsExpanded(false);
                }}
                className={`w-full px-4 py-3 text-left hover:bg-slate-700 transition-all
                  ${option.value === value ? 'bg-slate-700' : ''}`}
              >
                <div className="flex items-start gap-3">
                  <span className="text-2xl mt-1">{option.icon}</span>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium">{option.label}</span>
                      <span className={`text-xs font-medium ${getStatusColor(option.status)}`}>
                        {getStatusLabel(option.status)}
                      </span>
                    </div>
                    <div className="text-sm text-slate-400 mt-1">{option.description}</div>
                    <div className="mt-2">
                      <div className="text-xs text-slate-500 mb-1">필요 사항:</div>
                      <div className="flex flex-wrap gap-2">
                        {option.requirements.map(req => (
                          <span 
                            key={req}
                            className="text-xs px-2 py-1 bg-slate-900 text-slate-300 rounded"
                          >
                            {req}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  {option.value === value && (
                    <svg className="w-5 h-5 text-indigo-400 mt-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Provider info display */}
      <div className="mt-3 p-3 bg-slate-900/50 rounded-lg">
        <div className="text-xs text-slate-400">
          <div className="flex items-center justify-between mb-2">
            <span>현재 선택: <span className="text-slate-300 font-medium">{selectedOption.label}</span></span>
            <span className={getStatusColor(selectedOption.status)}>
              {getStatusLabel(selectedOption.status)}
            </span>
          </div>
          {selectedOption.status === 'needs-config' && (
            <div className="text-amber-400">
              ⚠️ Google Cloud Console에서 API 키와 Project ID 설정이 필요합니다
            </div>
          )}
          {selectedOption.status === 'paid-only' && (
            <div className="text-orange-400">
              💳 Google AI Studio에서 유료 플랜 업그레이드가 필요합니다 (Pay-as-you-go)
            </div>
          )}
        </div>
      </div>
    </div>
  );
};